import React, { useMemo, useRef, useEffect, Fragment } from 'react';
import cn from 'classnames';
import IconFilters from '@creditflowresearch/uilib/Icon/IconFilters';
import useIsMounted from '@framework/hooks/useIsMounted';
import { usePortfoliosContext } from '@/creditflowresearch/application/PortfolioMonitor/PortfoliosContext';
import { usePortfolioMonitorContext } from '@/creditflowresearch/application/PortfolioMonitor/PortfolioMonitorContext';

import styles from './portfolioTabs.module.scss';

interface IProps {
  onManageClick: () => void,
}

interface IPortfolioTab {
  key: string,
  name: string,
  isDisabled: boolean,
}

const PortfolioTabs = ({ onManageClick }: IProps) => {
  const {
    selectedPortfolio,
    setSelectedPortfolio,
    portfolioCollection,
  } = usePortfoliosContext();
  const { isFormOpened } = usePortfolioMonitorContext();

  const portfolioTabs: IPortfolioTab[] = useMemo(() => portfolioCollection.map((portfolio) => ({
    key: portfolio.name,
    name: portfolio.name,
    isDisabled: portfolio.id === selectedPortfolio?.id,
  })), [portfolioCollection, selectedPortfolio]);

  const isMounted = useIsMounted();
  const tabsRef = useRef({});

  const onSelect = (name: string) => {
    setSelectedPortfolio(portfolioCollection.find((item) => item.name === name));
  };

  const onTabClick = (tab) => {
    onSelect(tab.name);
  };

  useEffect(() => {
    const selectedKey = selectedPortfolio?.name;
    const selectedTabElement = tabsRef.current[selectedKey];
    if (selectedTabElement) {
      selectedTabElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'start',
      });
    }
  }, [selectedPortfolio, isMounted]);

  const getPortfolioTabs = (tabs: IPortfolioTab[]) => tabs.map((tab) => (
    <Fragment key={ tab.key }>
      <div
        ref={ (el) => { tabsRef.current[tab.key] = el; } }
        className={ cn(styles.tab, tab.isDisabled ? styles.selectedTab : '') }
        onClick={ () => onTabClick(tab) }
      >
        <div className={ styles.tabContent }>
          { tab.name }
        </div>
      </div>
      <div className={ styles.verticalLine } />
    </Fragment>
  ));

  return (
    <div className={ styles.wrapper } data-test="portfolioMonitorPortfolioTabs">
      <div className={ styles.tabs }>
        { isFormOpened ? (
          <div
            className={ cn(styles.tab, styles.selectedTab) }
          >
            <div className={ styles.tabContent }>
              { selectedPortfolio?.name }
            </div>
          </div>
        ) : getPortfolioTabs(portfolioTabs) }
      </div>
      { !isFormOpened && (
        <div
          className={ styles.manageContainer }
          onClick={ onManageClick }
        >
          <IconFilters className={ styles.manageIcon } />
        </div>
      ) }
    </div>
  );
};

export default PortfolioTabs;
